import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, Button, ActionIcon } from '@mantine/core';
import { IconUsers, IconPlus, IconX, IconCheck } from '@tabler/icons-react';
import { useUsers } from '@/hooks/api-hooks';
import { User } from '@turizi/shared';
import { getUserEditionGroups } from '@/lib/edition-groups-utils';
import CardModal from '@/components/card-modal';

export interface EditionGroupSelectorProps {
    value?: string;
    onChange: (group: string) => void;
    currentUserId?: number | null;
    disabled?: boolean;
}

export function EditionGroupSelector({
    value = '',
    onChange,
    currentUserId,
    disabled = false,
}: EditionGroupSelectorProps) {
    const { t } = useTranslation();
    const { data: usersResponse } = useUsers();
    const users: User[] = usersResponse?.data || [];

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [customGroups, setCustomGroups] = useState<string[]>([]);

    const membersByGroup = useMemo(() => {
        const map: Record<string, User[]> = {};
        users.forEach((u) => {
            const groups = getUserEditionGroups(u.permissions);
            groups.forEach((g) => {
                const key = g.toLowerCase().trim();
                if (!map[key]) {
                    map[key] = [];
                }
                map[key].push(u);
            });
        });
        return map;
    }, [users]);

    const allGroups = useMemo(() => {
        const groupsMap = new Map<string, string>();

        users.forEach((u) => {
            const groups = getUserEditionGroups(u.permissions);
            groups.forEach((g) => {
                const key = g.toLowerCase().trim();
                if (!groupsMap.has(key)) {
                    groupsMap.set(key, g.trim());
                }
            });
        });

        customGroups.forEach((g) => {
            const key = g.toLowerCase().trim();
            if (!groupsMap.has(key)) {
                groupsMap.set(key, g.trim());
            }
        });

        if (value && value.trim()) {
            const key = value.toLowerCase().trim();
            if (!groupsMap.has(key)) {
                groupsMap.set(key, value.trim());
            }
        }

        return Array.from(groupsMap.values()).sort((a, b) => a.localeCompare(b));
    }, [users, customGroups, value]);

    const handleCreateGroup = () => {
        const trimmed = newGroupName.trim();
        if (!trimmed || disabled) return;

        const existingKey = trimmed.toLowerCase();
        const existingGroup = allGroups.find((g) => g.toLowerCase() === existingKey);
        const resolvedGroup = existingGroup || trimmed;

        if (!existingGroup) {
            setCustomGroups((prev) => [...prev, trimmed]);
        }

        onChange(resolvedGroup);
        setNewGroupName('');
        setIsModalOpen(false);
    };

    const normalizedSelectedKey = value ? value.toLowerCase().trim() : '';
    const currentGroupMembers = normalizedSelectedKey ? membersByGroup[normalizedSelectedKey] || [] : [];
    const otherMembers = currentGroupMembers.filter((u) => !currentUserId || Number(u.id) !== Number(currentUserId));

    return (
        <div className="space-y-4 pt-1">
            <div className="text-xs text-gray-500 leading-relaxed">
                {t('edition_group_desc')}
            </div>

            {/* Currently Selected Group Card */}
            {value ? (
                <div className="p-3.5 rounded-xl border border-neutral-200 bg-neutral-50/80 shadow-2xs space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded-lg bg-neutral-200 text-neutral-800">
                                <IconUsers size={16} />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-neutral-500">
                                        {t('current_edition_group')}:
                                    </span>
                                    <span className="font-bold text-neutral-900">
                                        {value}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {!disabled && (
                            <ActionIcon
                                size="sm"
                                color="gray"
                                variant="subtle"
                                onClick={() => onChange('')}
                                title={t('clear_edition_group')}
                            >
                                <IconX size={14} />
                            </ActionIcon>
                        )}
                    </div>

                    <div className="text-xs text-neutral-600 pt-1.5 border-t border-neutral-200/70">
                        <span className="font-semibold text-neutral-800">{t('users_in_group')}: </span>
                        {otherMembers.length > 0 ? (
                            <span>{otherMembers.map((m) => m.name || m.email).join(', ')}</span>
                        ) : (
                            <span className="italic text-neutral-400">{t('no_other_users_in_group')}</span>
                        )}
                    </div>
                </div>
            ) : (
                <div className="p-3 rounded-xl border border-neutral-200 bg-neutral-50/80 text-xs text-neutral-600 leading-relaxed">
                    {t('no_edition_group_selected')}
                </div>
            )}

            {/* Existing Groups Section Header */}
            <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                        {t('existing_edition_groups')}
                    </div>

                    {!disabled && (
                        <Button
                            size="xs"
                            color="black"
                            variant="light"
                            radius="md"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => {
                                setNewGroupName('');
                                setIsModalOpen(true);
                            }}
                        >
                            {t('create_edition_group')}
                        </Button>
                    )}
                </div>

                {allGroups.length === 0 ? (
                    <div className="p-4 rounded-xl border border-dashed border-neutral-200 bg-neutral-50/50 text-center space-y-2.5">
                        <div className="text-xs text-neutral-400">
                            {t('no_edition_groups_created')}
                        </div>
                        {!disabled && (
                            <div>
                                <Button
                                    size="xs"
                                    color="black"
                                    variant="outline"
                                    radius="md"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={() => {
                                        setNewGroupName('');
                                        setIsModalOpen(true);
                                    }}
                                >
                                    {t('create_edition_group')}
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {allGroups.map((group) => {
                            const isSelected = normalizedSelectedKey === group.toLowerCase().trim();
                            const memberCount = (membersByGroup[group.toLowerCase().trim()] || []).length;
                            return (
                                <div
                                    key={group}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-all select-none cursor-pointer border ${
                                        isSelected
                                            ? 'bg-neutral-900 text-white border-neutral-900 font-semibold shadow-2xs'
                                            : 'bg-white hover:bg-neutral-100 text-neutral-700 border-neutral-200'
                                    } ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
                                    onClick={() => {
                                        if (disabled) return;
                                        onChange(isSelected ? '' : group);
                                    }}
                                >
                                    {isSelected ? <IconCheck size={13} /> : <IconUsers size={13} className="text-neutral-400" />}
                                    <span>{group}</span>
                                    {memberCount > 0 && (
                                        <span className={`text-[11px] ${isSelected ? 'text-neutral-300' : 'text-neutral-400'}`}>
                                            ({memberCount})
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal for Creating New Group */}
            <CardModal
                opened={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setNewGroupName('');
                }}
                size="sm"
                header={
                    <div className="flex items-center gap-2">
                        <IconUsers size={18} className="text-gray-700" />
                        <span className="font-semibold text-sm">{t('create_edition_group')}</span>
                    </div>
                }
                footer={
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            variant="subtle"
                            color="gray"
                            size="xs"
                            onClick={() => {
                                setIsModalOpen(false);
                                setNewGroupName('');
                            }}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            size="xs"
                            disabled={!newGroupName.trim()}
                            onClick={handleCreateGroup}
                        >
                            {t('save')}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-3">
                    <TextInput
                        label={t('edition_group')}
                        placeholder={t('edition_group_placeholder')}
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.currentTarget.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCreateGroup();
                            }
                        }}
                        radius="md"
                        data-autofocus
                        leftSection={<IconUsers size={14} className="text-gray-400" />}
                    />
                </div>
            </CardModal>
        </div>
    );
}

export default EditionGroupSelector;
